"use client";

import React from "react";
import { Check, Quote } from "lucide-react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
interface FounderData {
  id: string;
  name: string;
  badge: string;
  role: string;
  quote: string;
  bio: string;
  bullet1: string;
  bullet2: string;
  bullet3: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
}
const founders: FounderData[] = [{
  id: "scott",
  name: "Scott",
  badge: "Creative Director",
  role: "Co-Founder • Aesthetic Curation & Cultural Storytelling",
  quote: "Creative direction, bespoke lookbooks, cultural immersion, and sensory legacy design.",
  bio: "Scott curates the emotional resonance of the journey. From private after-hours museum entries and villa culinary sequences to personalized print lookbooks, he crafts immersive narrative moments that bridge generational gaps and leave lasting family memories.",
  bullet1: "Custom legacy lookbooks and destination narratives",
  bullet2: "Private estate curation and heritage access",
  bullet3: "Bespoke sensory dining and multi-age milestone events",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/ey-scott-leete-v2.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/ey-scott-leete-v2.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/ey-scott-leete-v2.webp-wvc-sizes",
  imageAlt: "Portrait of Scott, Co-Founder and Creative Director"
}, {
  id: "michael",
  name: "Michael",
  badge: "Logistical Co-Pilot",
  role: "Co-Founder • Logistical Architecture & Risk Mitigation",
  quote: "Logistical matrices, supplier contract negotiations, and multi-generational risk mitigation.",
  bio: "Michael engineers the invisible structural spine of every itinerary. Managing complex travel mechanics, tight flight-to-yacht transfers, and supplier contingencies, he ensures that multi-generational groups move effortlessly across borders without logistical friction or operational surprises.",
  bullet1: "Dynamic multi-time-zone itinerary matrices",
  bullet2: "Direct luxury supplier and private aviation negotiations",
  bullet3: "Proactive contingency planning and health protocols",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/Michael_photo.jpg",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/Michael_photo.jpg-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/Michael_photo.jpg-wvc-sizes",
  imageAlt: "Portrait of Michael, Co-Founder and Logistical Co-Pilot"
}];
export default function Founders() {
  return <section id="foundersprofile" data-nav="light" className="bg-background text-foreground border-b border-border py-20 lg:py-28">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="mx-auto max-w-3xl text-center mb-16 lg:mb-20">
          <div className="flex justify-center mb-4">
            <Badge variant="outline" className="px-3.5 py-1 text-xs tracking-wider uppercase font-semibold text-primary border-primary/30 bg-accent/50">
              The Travel Design Duo
            </Badge>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground tracking-tight leading-tight mb-6">
            Two distinct disciplines, one seamless family legacy
          </h2>

          <p className="text-base sm:text-lg text-foreground/80 leading-relaxed font-sans">
            We believe extraordinary multi-generational travel requires equal parts ruthless logistical precision and poetic creative curation. We oversee every milestone personally from first blueprint to homecoming.
          </p>
        </motion.div>

        {/* Founders Two-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          {founders.map((founder, i) => <motion.div key={founder.id} data-index={i} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: i * 0.15,
          ease: "easeOut"
        }} className="h-full">
              <Card className="h-full border border-border bg-card text-card-foreground rounded-xl shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 p-0 overflow-hidden flex flex-col justify-between">
                <div>
                  {/* Portrait & Core Identity */}
                  <div className="p-6 sm:p-8 pb-4">
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-6">
                      <div className="relative shrink-0 w-28 h-28 sm:w-32 sm:h-32 rounded-xl overflow-hidden border border-border bg-muted">
                        <img src={founder.imageSrc} data-wvc-srcset={founder.imageSrcset} data-wvc-sizes={founder.imageSizes} alt={founder.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      </div>

                      <div className="text-center sm:text-left">
                        <Badge variant="secondary" className="mb-2 text-xs font-medium bg-muted text-muted-foreground">
                          {founder.badge}
                        </Badge>
                        <h3 className="text-2xl sm:text-3xl font-serif font-bold text-foreground">
                          {founder.name}
                        </h3>
                        <p className="text-sm font-medium text-foreground/75 mt-1">
                          {founder.role}
                        </p>
                      </div>
                    </div>

                    {/* Focus Quote */}
                    <div className="rounded-lg bg-accent/40 border border-border/70 p-4 sm:p-5 mb-6 relative">
                      <Quote className="size-4 text-primary absolute top-4 left-3.5 opacity-40" />
                      <p className="text-sm sm:text-base font-serif italic text-foreground leading-relaxed pl-5">
                        "{founder.quote}"
                      </p>
                    </div>

                    {/* Bio */}
                    <p className="text-sm sm:text-base text-foreground/85 leading-relaxed mb-6 font-sans">
                      {founder.bio}
                    </p>
                  </div>
                </div>

                {/* Domain of Stewardship List */}
                <CardContent className="px-6 sm:px-8 pb-8 pt-0 mt-auto">
                  <div className="pt-5 border-t border-border">
                    <h4 className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-foreground mb-3 font-sans">
                      Primary domain of stewardship
                    </h4>

                    <ul className="space-y-2.5">
                      <li className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90 font-sans">
                        <span className="shrink-0 rounded-full bg-accent text-primary p-0.5 mt-0.5">
                          <Check className="size-3.5" />
                        </span>
                        <span>{founder.bullet1}</span>
                      </li>
                      <li className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90 font-sans">
                        <span className="shrink-0 rounded-full bg-accent text-primary p-0.5 mt-0.5">
                          <Check className="size-3.5" />
                        </span>
                        <span>{founder.bullet2}</span>
                      </li>
                      <li className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90 font-sans">
                        <span className="shrink-0 rounded-full bg-accent text-primary p-0.5 mt-0.5">
                          <Check className="size-3.5" />
                        </span>
                        <span>{founder.bullet3}</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>
      </div>
    </section>;
}